// Thin MediaRecorder wrapper with explicit state. The AudioContext is
// owned by AudioSessionManager — this class deliberately does NOT
// touch any audio-session category or AudioContext state; the iOS
// spike showed that AudioSessionManager.unlock() before mic access
// is enough to keep playback working post-record.

export default class MicRecorder {
  constructor(audioSessionManager) {
    this._asm = audioSessionManager;
    this._state = 'idle';
    this._listeners = new Map();
    this._mediaRecorder = null;
    this._stream = null;
    this._chunks = [];
    this._mimeType = null;
    this._startedAtMs = null;
    this._stopDeferred = null;
  }

  getState() {
    return this._state;
  }

  on(event, callback) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(callback);
  }

  off(event, callback) {
    const set = this._listeners.get(event);
    if (set) set.delete(callback);
  }

  _emit(event, payload) {
    const set = this._listeners.get(event);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error('[MicRecorder] listener threw:', e);
      }
    }
  }

  _setState(next) {
    if (this._state === next) return;
    this._state = next;
    this._emit('stateChange', next);
  }

  async start() {
    if (this._state !== 'idle') {
      throw new Error(
        `MicRecorder.start() called in state '${this._state}' (expected 'idle')`
      );
    }
    this._setState('requesting');
    try {
      this._stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      this._chunks = [];
      // iOS Safari/Chrome (both WebKit) advertise webm support via
      // MediaRecorder.isTypeSupported('audio/webm') but the resulting
      // container is malformed — Whisper transcribes it as duration:0
      // with empty text. Force audio/mp4 on iOS to get a valid MP4/AAC
      // container. See src/pages/VoiceTest.jsx for the original
      // workaround that documented this.
      const isIOS =
        typeof navigator !== 'undefined' &&
        /iPad|iPhone|iPod/i.test(navigator.userAgent) &&
        typeof window !== 'undefined' &&
        !window.MSStream;
      const recorderOptions =
        isIOS &&
        typeof MediaRecorder !== 'undefined' &&
        typeof MediaRecorder.isTypeSupported === 'function' &&
        MediaRecorder.isTypeSupported('audio/mp4')
          ? { mimeType: 'audio/mp4' }
          : undefined;
      this._mediaRecorder = recorderOptions
        ? new MediaRecorder(this._stream, recorderOptions)
        : new MediaRecorder(this._stream);
      this._mimeType = this._mediaRecorder.mimeType || 'audio/webm';
      this._mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this._chunks.push(e.data);
      };
      this._mediaRecorder.onerror = (e) => {
        const error = e.error || new Error('MediaRecorder error');
        this._setState('error');
        this._emit('error', { error });
        this._releaseTracks();
      };
      this._mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this._chunks, { type: this._mimeType });
        const durationMs = this._startedAtMs
          ? Date.now() - this._startedAtMs
          : 0;
        this._chunks = [];
        this._releaseTracks();
        if (this._stopDeferred) {
          const d = this._stopDeferred;
          this._stopDeferred = null;
          if (d.cancelled) {
            this._setState('idle');
            d.resolve({ audioBlob: null, durationMs });
          } else {
            this._setState('idle');
            this._emit('stop', { audioBlob, durationMs });
            d.resolve({ audioBlob, durationMs });
          }
        } else {
          this._setState('idle');
          this._emit('stop', { audioBlob, durationMs });
        }
      };
      this._startedAtMs = Date.now();
      this._mediaRecorder.start();
      this._setState('recording');
      this._emit('start');
    } catch (error) {
      this._setState('error');
      this._emit('error', { error });
      this._releaseTracks();
      throw error;
    }
  }

  async stop() {
    if (this._state !== 'recording') {
      throw new Error(
        `MicRecorder.stop() called in state '${this._state}' (expected 'recording')`
      );
    }
    this._setState('stopping');
    const deferred = makeDeferred();
    this._stopDeferred = deferred;
    this._mediaRecorder.stop();
    return deferred.promise;
  }

  cancel() {
    if (this._state !== 'recording' && this._state !== 'stopping') {
      this._releaseTracks();
      this._setState('idle');
      return;
    }
    if (this._stopDeferred) this._stopDeferred.cancelled = true;
    if (
      this._mediaRecorder &&
      this._mediaRecorder.state !== 'inactive'
    ) {
      try {
        this._mediaRecorder.stop();
      } catch {}
    } else {
      this._releaseTracks();
      this._setState('idle');
    }
  }

  _releaseTracks() {
    if (this._stream) {
      try {
        this._stream.getTracks().forEach((t) => t.stop());
      } catch {}
      this._stream = null;
    }
    this._mediaRecorder = null;
  }
}

function makeDeferred() {
  let resolve, reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject, cancelled: false };
}
